<script lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import InputWrapper from './Inputs/InputWrapper.vue'

let nextNumberFieldId = 0

/** Returns an error message when invalid; any falsy value means valid. */
export type NumberFieldRule = (value: number | null) => string | false | null | undefined

/** Contract `VsForm` expects from the inputs it validates on submit. */
interface VsFormField {
  _uid: string
  readonly name: string | undefined
  runValidation: () => boolean
  addErrorMessages: (messages: string[]) => void
  resetErrorMessages: () => void
}

interface VsFormRegistration {
  register: (field: VsFormField) => void
  unregister: (field: VsFormField) => void
}
</script>

<script setup lang="ts">
interface NumberFieldProps {
  label: string
  disabled?: boolean
  errorMessages?: string[]
  hint?: string
  max?: number
  min?: number
  name?: string
  prefix?: string
  required?: boolean
  rules?: NumberFieldRule[]
  step?: number | 'any'
  suffix?: string
  value?: number | null
}

const props = withDefaults(defineProps<NumberFieldProps>(), {
  disabled: false,
  errorMessages: () => [],
  hint: undefined,
  max: undefined,
  min: undefined,
  name: undefined,
  prefix: undefined,
  required: false,
  rules: () => [],
  step: undefined,
  suffix: undefined,
  value: null,
})

const emit = defineEmits<{
  (event: 'input', value: number | null): void
  (event: 'blur', value: number | null): void
  (event: 'focus', value: FocusEvent): void
}>()

const inputId = `number-field-${++nextNumberFieldId}`
const hintId = `${inputId}-hint`
const errorId = `${inputId}-error`
const inputRef = ref<HTMLInputElement | null>(null)
const focused = ref(false)
const displayValue = ref(String(props.value ?? ''))
const lastEmittedValue = ref<number | null | undefined>(undefined)
const localErrors = ref<string[]>([])

watch(
  () => props.value,
  (value) => {
    const isEcho = Object.is(value, lastEmittedValue.value)
    lastEmittedValue.value = undefined
    // An external change (e.g. a form reset) invalidates errors from the last
    // blur; an echo of our own emit must keep them.
    if (!isEcho) localErrors.value = []
    // The parent's numeric echo should not replace text still being edited.
    if (focused.value && isEcho) return
    displayValue.value = String(value ?? '')
  },
)

const messages = computed(() => [...props.errorMessages, ...localErrors.value])
const describedBy = computed(() => {
  if (messages.value.length) {
    return messages.value.map((_, index) => `${errorId}-${index}`).join(' ')
  }
  return props.hint ? hintId : undefined
})

function validate(value: number | null): string[] {
  if (props.required && value === null) return ['This field is required']
  return props.rules
    .map((rule) => rule(value))
    .filter((message): message is string => typeof message === 'string' && message.length > 0)
}

function numericValue(input: HTMLInputElement): number | null {
  return input.value === '' || Number.isNaN(input.valueAsNumber) ? null : input.valueAsNumber
}

function handleInput(event: Event) {
  const input = event.target as HTMLInputElement
  displayValue.value = input.value
  localErrors.value = []
  const value = numericValue(input)
  lastEmittedValue.value = value
  emit('input', value)
}

function handleFocus(event: FocusEvent) {
  focused.value = true
  emit('focus', event)
}

function handleBlur(event: FocusEvent) {
  focused.value = false
  const input = event.target as HTMLInputElement
  const value = numericValue(input)
  localErrors.value = validate(value)
  displayValue.value = String(value ?? '')
  lastEmittedValue.value = value
  emit('blur', value)
  // A parent that commits on blur echoes within this flush. Expire the
  // pending echo afterwards so a later external reset to the same value
  // (e.g. a form reset to null) is treated as external and clears errors.
  nextTick(() => {
    lastEmittedValue.value = undefined
  })
}

function focus() {
  inputRef.value?.focus()
}

function select() {
  inputRef.value?.select()
}

function getInputElement() {
  return inputRef.value
}

// Register with an enclosing VsForm so `isValid()` covers this field on submit.
const form = inject<VsFormRegistration | null>('VsFormRegistration', null)
const formField: VsFormField = {
  _uid: inputId,
  get name() {
    return props.name
  },
  runValidation() {
    localErrors.value = validate(props.value)
    return localErrors.value.length > 0
  },
  addErrorMessages(messages) {
    localErrors.value = messages
  },
  resetErrorMessages() {
    localErrors.value = []
  },
}
onMounted(() => form?.register(formField))
onBeforeUnmount(() => form?.unregister(formField))

defineExpose({ focus, select, getInputElement })
</script>

<template>
  <InputWrapper
    :label="label"
    :name="name"
    :disabled="disabled"
    :mini-label="Boolean(displayValue) || focused"
    :hint="hint"
    :error-messages="messages"
    :input-id="inputId"
    :hint-id="hintId"
    :error-id="errorId"
  >
    <span v-if="prefix" class="prefix" aria-hidden="true">{{ prefix }}</span>
    <input
      :id="inputId"
      ref="inputRef"
      class="number-input"
      :class="{ 'has-prefix': prefix, 'has-suffix': suffix }"
      type="number"
      :name="name"
      :value="displayValue"
      :placeholder="label"
      :disabled="disabled"
      :min="min"
      :max="max"
      :step="step"
      :aria-required="required ? 'true' : undefined"
      :aria-invalid="messages.length ? 'true' : undefined"
      :aria-describedby="describedBy"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <template v-if="suffix" #append>
      <span class="suffix" aria-hidden="true">{{ suffix }}</span>
    </template>
  </InputWrapper>
</template>

<style scoped>
.number-input.number-input[type='number'] {
  flex: 0 1 auto;
  width: auto;
  field-sizing: content;
  text-align: left;
}

.number-input.has-prefix {
  padding-left: 0.25rem;
}

.number-input.has-suffix {
  padding-right: 0.25rem;
}

.prefix {
  display: flex;
  align-items: center;
  padding-left: var(--space-base);
  white-space: nowrap;
  font-weight: 600;
  color: var(--color-grey);
}

.suffix {
  white-space: nowrap;
  font-weight: 600;
  color: var(--color-grey);
}
</style>
